import { QRCodeSVG } from 'qrcode.react'
import { isValidIBAN } from 'ibantools'
import { useState } from 'react'

import { formatAmount } from '~/@lib/utils/number.utils'
import { generateBySquareCode } from '~/@lib_integrations/bysquare/generateBySquareCode'
import { useUser } from '~/@biz_account/useUser/useUser'
import { Text } from '~/@mid_ui/typography/Text'
import { useNavigate } from 'react-router-dom'
import { GraphIcon } from '~/@mid_ui/icons/GraphIcon'
import { CurrencyInput } from '~/@mid_ui-form/currency-input/CurrencyInput'
import { LabelValueItem } from '~/@mid_ui/label-value/LabelValueItem'
import { useCompanyInfo } from '~/@mid_settings/company-info/useCompanyInfo'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { BackButton } from '~/@pages_investing/project-detail/project-detail_ui/back-button/BackButton'

function AddFundsForm() {
  const [amount, setAmount] = useState(50)
  const { user } = useUser()
  const companyInfo = useCompanyInfo()

  const navigate = useNavigate()

  const isFundsOperationsEnabled = user?.isWithdrawAndAddFundsEnabled
  const mainInvestorWallet = user?.investorWallet

  const qrCodeContent = (() => {
    if (!companyInfo || !user || amount <= 0) return null

    if (!isValidIBAN(companyInfo.iban))
      return (
        <div className="p-10 border border-primary-500">
          <Text>Neplatný IBAN</Text>
        </div>
      )

    return (
      <div className="p-10 border border-primary-500">
        <QRCodeSVG
          value={generateBySquareCode({
            amount,
            paymentNote: `${user.profile.sign} ${user.profile.fullname}`,
            beneficiary: { name: companyInfo?.companyName },
            bankAccounts: [
              { iban: companyInfo?.iban, bic: companyInfo?.swift },
            ],
            variableSymbol: user.profile.sign,
          })}
          size={250}
        />
      </div>
    )
  })()

  if (!isFundsOperationsEnabled || !mainInvestorWallet?.csob) {
    navigate('/account')
    return null
  }

  return (
    <div className="flex flex-col items-center justify-center w-full my-10">
      <div className="flex flex-col gap-10 max-w-[600px] items-baseline">
        <BackButton className="text-primary" />

        <div className="flex flex-col gap-5">
          <HeadingTwo>Vklad</HeadingTwo>
          <Text className="font-semibold">
            Preveďte prostriedky na svoj účet jednoduchým bankovým prevodom.
          </Text>
        </div>
        <Text className="font-semibold">
          Po uskutočnení prevodu z Vášho bankového účtu na účet Invest in
          Slovakia podľa variabilného symbolu spárujeme platbu s Vaším
          investičným účtom.
        </Text>

        <CurrencyInput
          autoFocus
          labelText="Čiastka ktorú chcete vložiť"
          value={amount}
          onChangeValue={setAmount}
        />

        {companyInfo && (
          <div className="flex flex-col gap-5 p-5 border border-accent w-full">
            <div className="flex flex-row justify-between items-center">
              <Text className="font-semibold">Údaje pre bankový prevod</Text>
              <GraphIcon />
            </div>
            <div className="flex flex-col gap-2 items-start">
              <LabelValueItem
                label={'Kam vkladáte'}
                value={'Investičný účet'}
                labelClassName="text-sm font-semibold"
                contentClassName="text-accent font-semibold"
              />
              {[
                {
                  label: 'Spoločnosť',
                  value: companyInfo?.companyName,
                },
                {
                  label: 'Meno banky',
                  value: companyInfo?.bankName,
                },
                { label: 'IBAN', value: companyInfo?.iban },
                { label: 'SWIFT', value: companyInfo?.swift },
                {
                  label: 'Váš variabilný symbol',
                  value: mainInvestorWallet.csob.variableSymbol,
                },
                {
                  label: 'Čiastka',
                  value: formatAmount(amount, { maximumFractionDigits: 2 }),
                },
              ].map(({ label, value }) => (
                <LabelValueItem
                  label={label}
                  value={value}
                  labelClassName="text-sm font-semibold"
                  contentClassName="text-accent font-semibold"
                  key={label}
                />
              ))}
            </div>
          </div>
        )}

        {qrCodeContent}
      </div>
    </div>
  )
}

export { AddFundsForm }
